import { useApplicationDocuments } from '@/hooks/useApplicationDocuments';
import { ExistingDocumentLink } from './ExistingDocumentLink';
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, ExternalLink, User, Building, Mail, FileText } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { agentApi } from '@/services/agentApi';

interface ReviewStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
  applicationData?: any; // Optional: if application was loaded with university/program data
  draftApplicationId?: number | null;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
  applicationData,
  draftApplicationId,
}) => {
  const { user } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [universityName, setUniversityName] = useState<string>('');
  const [programName, setProgramName] = useState<string>('');
  const [loadingUniversity, setLoadingUniversity] = useState(false);
  const [loadingProgram, setLoadingProgram] = useState(false);
  const existing = useApplicationDocuments(draftApplicationId, formData.documents);
  const uploadedDocuments = existing.matched;
  const loadingDocuments = existing.loading;

  // Initialize from applicationData if available (when loading a draft)
  useEffect(() => {
    if (applicationData?.universities?.name && !universityName) {
      setUniversityName(applicationData.universities.name);
    }
    if (applicationData?.university_programs?.programs?.name && !programName) {
      const prog = applicationData.university_programs.programs;
      const name = prog.name;
      const level = prog.level || '';
      setProgramName(level ? `${name} - ${level}` : name);
    }
  }, [applicationData]);

  // Load university name
  useEffect(() => {
    if (formData.universityId) {
      loadUniversityInfo();
    } else {
      setUniversityName('');
    }
  }, [formData.universityId]);

  // Load program name
  useEffect(() => {
    if (formData.universityId && formData.universityProgramId) {
      loadProgramInfo();
    } else {
      setProgramName('');
    }
  }, [formData.universityId, formData.universityProgramId]);

  const loadUniversityInfo = async () => {
    if (!formData.universityId) return;
    
    // Skip if we already have the name from applicationData
    if (universityName) return;
    
    setLoadingUniversity(true);
    try {
      const targetId = Number(formData.universityId);
      
      // Try direct API call first (most reliable)
      try {
        const university = await agentApi.getUniversityById(targetId);
        if (university && university.name) {
          setUniversityName(university.name);
          setLoadingUniversity(false);
          return;
        }
      } catch (err) {
        console.warn('Error fetching university directly:', err);
      }
      
      // Fallback: try approved institutions
      try {
        const institutions = await agentApi.getApprovedInstitutions();
        const institution = institutions.find(inst => {
          const instId = Number(inst.id);
          return instId === targetId;
        });
        
        if (institution && institution.name) {
          setUniversityName(institution.name);
          setLoadingUniversity(false);
          return;
        }
      } catch (err) {
        console.warn('Error loading approved institutions:', err);
      }
      
      // Last fallback: try all universities API
      try {
        const allUniversitiesResponse = await agentApi.getUniversities({});
        const allUniversities = allUniversitiesResponse.universities || 
                               (Array.isArray(allUniversitiesResponse) ? allUniversitiesResponse : []);
        
        const univ = allUniversities.find((u: any) => Number(u.id) === targetId);
        
        if (univ && univ.name) {
          setUniversityName(univ.name);
          setLoadingUniversity(false);
          return;
        }
      } catch (err) {
        console.warn('Error loading all universities:', err);
      }
      
      // If all else fails, show the ID
      setUniversityName(`University ID: ${formData.universityId}`);
    } catch (error) {
      console.error('Error loading university info:', error);
      setUniversityName(`University ID: ${formData.universityId}`);
    } finally {
      setLoadingUniversity(false);
    }
  };

  const loadProgramInfo = async () => {
    if (!formData.universityId || !formData.universityProgramId) return;
    
    setLoadingProgram(true);
    try {
      const data = await agentApi.getUniversityPrograms(formData.universityId);
      const programs = data.programs || data || [];
      
      // Normalize IDs for comparison
      const targetProgramId = Number(formData.universityProgramId);
      
      // Try to find program with flexible ID matching
      const program = programs.find((p: any) => {
        const programId = p.id || p.programs?.id;
        const numProgramId = Number(programId);
        return numProgramId === targetProgramId || programId === formData.universityProgramId;
      });
      
      if (program) {
        const name = program.programs?.name || program.name;
        const level = program.programs?.level || program.level;
        if (name) {
          setProgramName(level ? `${name} - ${level}` : name);
        } else {
          setProgramName(`Program ID: ${formData.universityProgramId}`);
        }
      } else {
        setProgramName(`Program ID: ${formData.universityProgramId}`);
      }
    } catch (error) {
      console.error('Error loading program info:', error);
      setProgramName(`Program ID: ${formData.universityProgramId}`);
    } finally {
      setLoadingProgram(false);
    }
  };

  useEffect(() => {
    // Clear terms error when accepted
    if (termsAccepted && errors.termsAccepted) {
      setErrors({ ...errors, termsAccepted: undefined });
    }
  }, [termsAccepted]);

  const handleTermsChange = (accepted: boolean) => {
    setTermsAccepted(accepted);
    updateFormData({ termsAccepted: accepted as any });
    if (errors.termsAccepted) {
      setErrors({ ...errors, termsAccepted: undefined });
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'Not provided';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return date;
    }
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">Review Application</h5>
      <p className="text-muted mb-3">
        Please review all information before submitting. You can go back to edit any section.
      </p>

      {/* A. Qualification You Are Applying For */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">A. Qualification You Are Applying For</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Institution:</strong>{' '}
              {!formData.universityId ? (
                <span className="text-danger">Required</span>
              ) : loadingUniversity ? (
                <span className="text-muted">Loading...</span>
              ) : universityName ? (
                universityName
              ) : (
                <span className="text-warning">Loading institution name...</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Programme Strand:</strong> {formData.programmeStrand || 'Not provided'}
            </div>
            {formData.programmeStartDate && (
              <div className="col-md-6 mb-2">
                <strong>Programme Start Date:</strong> {formatDate(formData.programmeStartDate)}
              </div>
            )}
            {formData.programmeEndDate && (
              <div className="col-md-6 mb-2">
                <strong>Programme End Date:</strong> {formatDate(formData.programmeEndDate)}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>Studied at Kalandra Before:</strong> {formData.studiedAtKalandraBefore ? 'Yes' : 'No'}
            </div>
            {formData.howDidYouFindOut && (
              <div className="col-md-6 mb-2">
                <strong>How Did You Find Out:</strong> {formData.howDidYouFindOut}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* B. Personal Details */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">B. Personal Details</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Name:</strong> {formData.preferredTitle} {formData.firstName} {formData.middleName} {formData.lastName}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Date of Birth:</strong> {formatDate(formData.dateOfBirth)}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Gender:</strong> {formData.gender || 'Not provided'}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Marital Status:</strong> {formData.maritalStatus || 'Not provided'}
            </div>
            {formData.nsn && (
              <div className="col-md-6 mb-2">
                <strong>NSN:</strong> {formData.nsn}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>Country of Citizenship:</strong> {formData.countryOfCitizenship || 'Not provided'}
            </div>
            {formData.ethnicity && (
              <div className="col-md-6 mb-2">
                <strong>Ethnicity:</strong> {formData.ethnicity}
              </div>
            )}
            {formData.iwi && (
              <div className="col-md-6 mb-2">
                <strong>Iwi:</strong> {formData.iwi}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>First Language:</strong> {formData.firstLanguage || 'Not provided'}
            </div>
            {formData.passportExpiryDate && (
              <div className="col-md-6 mb-2">
                <strong>Passport Expiry:</strong> {formatDate(formData.passportExpiryDate)}
              </div>
            )}
            {formData.disability && (
              <div className="col-12 mb-2">
                <strong>Disability:</strong> Yes - {formData.disabilityDetails || 'Details provided'}
              </div>
            )}
            {formData.convictionChecks && (
              <div className="col-12 mb-2">
                <strong>Conviction Checks:</strong> Yes - {formData.convictionDetails || 'Details provided'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* C. Contact Details */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">C. Contact Details</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>Email:</strong> {formData.email || <span className="text-danger">Required</span>}
            </div>
            {formData.homePhone && (
              <div className="col-md-6 mb-2">
                <strong>Home Phone:</strong> {formData.homePhone}
              </div>
            )}
            {formData.homeMobile && (
              <div className="col-md-6 mb-2">
                <strong>Home Mobile:</strong> {formData.homeMobile}
              </div>
            )}
            {formData.homeAddress && (
              <div className="col-12 mb-2">
                <strong>Home Address:</strong> {formData.homeAddress}
              </div>
            )}
            {formData.studyAddress && (
              <div className="col-12 mb-2">
                <strong>Study Address (NZ):</strong> {formData.studyAddress}
              </div>
            )}
            {formData.nextOfKinName && (
              <div className="col-md-6 mb-2">
                <strong>Next of Kin:</strong> {formData.nextOfKinName}
              </div>
            )}
            {formData.emergencyContactName && (
              <div className="col-md-6 mb-2">
                <strong>Emergency Contact (NZ):</strong> {formData.emergencyContactName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* D. Academic Information */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">D. Academic Information</h6>
        </div>
        <div className="card-body">
          <div className="row">
            {formData.secondarySchoolLocation && (
              <div className="col-md-6 mb-2">
                <strong>Secondary School Location:</strong>{' '}
                {formData.secondarySchoolLocation === 'new-zealand' ? 'New Zealand' : 'Overseas'}
              </div>
            )}
            {formData.secondarySchoolName && (
              <div className="col-md-6 mb-2">
                <strong>Secondary School:</strong> {formData.secondarySchoolName}
              </div>
            )}
            {formData.secondarySchoolLastYear && (
              <div className="col-md-6 mb-2">
                <strong>Last Year:</strong> {formData.secondarySchoolLastYear}
              </div>
            )}
            {formData.secondarySchoolHighestLevel && (
              <div className="col-md-6 mb-2">
                <strong>Highest Level:</strong> {formData.secondarySchoolHighestLevel}
              </div>
            )}
            <div className="col-md-6 mb-2">
              <strong>First Year Tertiary:</strong>{' '}
              {formData.firstYearTertiaryEnrolment === null
                ? 'Not provided'
                : formData.firstYearTertiaryEnrolment
                ? 'Yes'
                : 'No'}
            </div>
            {formData.tertiaryInstitutionName && (
              <div className="col-md-6 mb-2">
                <strong>Previous Tertiary Institution:</strong> {formData.tertiaryInstitutionName}
              </div>
            )}
            {formData.englishLevel && (
              <div className="col-md-6 mb-2">
                <strong>English Level:</strong> {formData.englishLevel}
              </div>
            )}
            {formData.careerIntentions && (
              <div className="col-12 mb-2">
                <strong>Career Intentions:</strong> {formData.careerIntentions}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Test Scores */}
      {(formData.hasEnglishScores || formData.hasGREScores || formData.hasGMATScores || formData.hasSATScores) && (
        <div className="card mb-3 border">
          <div className="card-header bg-light">
            <h6 className="mb-0 fw-semibold">Test Scores</h6>
          </div>
          <div className="card-body">
            {formData.hasEnglishScores && (
              <div className="mb-2">
                <strong>English Proficiency ({formData.englishExamType}):</strong> {formData.englishOverallScore || 'Not provided'}
              </div>
            )}
            {formData.hasGREScores && (
              <div className="mb-2">
                <strong>GRE:</strong> {formData.greOverallScore || 'Not provided'}
              </div>
            )}
            {formData.hasGMATScores && (
              <div className="mb-2">
                <strong>GMAT:</strong> {formData.gmatTotalScore || 'Not provided'}
              </div>
            )}
            {formData.hasSATScores && (
              <div className="mb-2">
                <strong>SAT:</strong> {formData.satOverallScore || 'Not provided'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Work History */}
      {(formData.workedAsHealthCareAssistant || formData.otherHealthcareQualifications) && (
        <div className="card mb-3 border">
          <div className="card-header bg-light">
            <h6 className="mb-0 fw-semibold">E. Work History</h6>
          </div>
          <div className="card-body">
            <div className="row">
              {formData.workedAsHealthCareAssistant && (
                <div className="col-12 mb-2">
                  <strong>Worked as Health Care Assistant:</strong> Yes
                  {formData.healthCareAssistantDuration && ` - ${formData.healthCareAssistantDuration}`}
                </div>
              )}
              {formData.otherHealthcareQualifications && (
                <div className="col-12 mb-2">
                  <strong>Other Healthcare Qualifications:</strong> Yes
                  {formData.healthcareQualificationsDetails && ` - ${formData.healthcareQualificationsDetails}`}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Health, Welfare and Accommodation */}
      {(formData.insuranceViaSouthernCross || formData.insuranceViaUniCare || formData.insuranceSelfArranged) && (
        <div className="card mb-3 border">
          <div className="card-header bg-light">
            <h6 className="mb-0 fw-semibold">F. Health, Welfare and Accommodation</h6>
          </div>
          <div className="card-body">
            <div className="row">
              {formData.insuranceViaSouthernCross && (
                <div className="col-12 mb-2">
                  <strong>Insurance Preference:</strong> Arrange insurance with Southern Cross through the institution
                </div>
              )}
              {formData.insuranceViaUniCare && (
                <div className="col-12 mb-2">
                  <strong>Insurance Preference:</strong> Arrange insurance with Uni-care through the institution
                </div>
              )}
              {formData.insuranceSelfArranged && (
                <div className="col-12 mb-2">
                  <strong>Insurance Preference:</strong> Student will organise their own insurance and provide proof prior to
                  arrival
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      {formData.whyDoYouWantToDoProgramme && (
        <div className="card mb-3 border">
          <div className="card-header bg-light">
            <h6 className="mb-0 fw-semibold">Additional Information</h6>
          </div>
          <div className="card-body">
            <p className="mb-0">{formData.whyDoYouWantToDoProgramme}</p>
          </div>
        </div>
      )}

      {/* Program Selection */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">Program Selection</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <strong>University:</strong>{' '}
              {!formData.universityId ? (
                <span className="text-danger">Required</span>
              ) : loadingUniversity ? (
                <span className="text-muted">Loading...</span>
              ) : universityName ? (
                universityName
              ) : (
                <span className="text-warning">Loading university name...</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <strong>Program:</strong>{' '}
              {!formData.universityProgramId ? (
                <span className="text-danger">Required</span>
              ) : loadingProgram ? (
                <span className="text-muted">Loading...</span>
              ) : programName ? (
                programName
              ) : (
                <span className="text-warning">Loading program name...</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Documents Checklist */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <FileText size={18} className="me-2" />
            Documents Checklist
          </h6>
        </div>
        <div className="card-body">
          {existing.error && <div className="alert alert-danger" role="alert">Could not load existing documents. <button type="button" onClick={existing.retry}>Retry</button></div>}
          {existing.metadataError && <div className="alert alert-warning">Saved document information could not be read. Existing attachments are retained; contact support to restore checklist associations.</div>}
          {existing.unmatched.length > 0 && <div className="mb-3"><h6>Other existing attachments</h6>{existing.unmatched.map(file => <div key={file.id}><ExistingDocumentLink applicationId={draftApplicationId} document={file} /></div>)}</div>}
          {existing.error ? null : loadingDocuments ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary me-2" />
              <span className="text-muted">Loading documents...</span>
            </div>
          ) : (
            <div className="row">
              {[
                { key: 'convictionCheck', label: 'Copy of Conviction Check', required: formData.convictionChecks },
                { key: 'passport', label: 'Copy of Passport', required: false },
                { key: 'visa', label: 'Copy of Visa (if applicable)', required: false },
                { key: 'transcripts', label: 'Copies of Certified Transcripts', required: false },
                { key: 'certificates', label: 'Copies of Certificates Awarded', required: false },
                { key: 'employmentContract', label: 'Evidence of Employment Contract', required: formData.workedAsHealthCareAssistant || formData.otherHealthcareQualifications },
                { key: 'workHistory', label: 'Work History Documents', required: false },
                { key: 'ieltsToefl', label: 'Evidence of IELTS/TOEFL Score', required: formData.hasEnglishScores },
              ].map((doc) => {
                const uploaded = uploadedDocuments[doc.key];
                return (
                  <div key={doc.key} className="col-md-6 mb-2">
                    <div className="d-flex align-items-center">
                      {uploaded ? (
                        <CheckCircle size={18} className="text-success me-2" />
                      ) : (
                        <XCircle size={18} className={doc.required ? 'text-danger me-2' : 'text-muted me-2'} />
                      )}
                      <div className="flex-grow-1">
                        <span className={uploaded ? 'text-success' : doc.required ? 'text-danger' : 'text-muted'}>
                          {doc.label}
                          {doc.required && <span className="text-danger ms-1">*</span>}
                        </span>
                        {uploaded && (
                          <div className="small text-muted ms-4">
                            <ExistingDocumentLink applicationId={draftApplicationId} document={uploaded} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {Object.keys(uploadedDocuments).length === 0 && existing.unmatched.length === 0 && !loadingDocuments && !existing.error && !existing.metadataError && (
            <div className="alert alert-info mb-0">
              <small>No documents have been uploaded yet. Documents can be uploaded in the Documents Checklist step.</small>
            </div>
          )}
        </div>
      </div>

      {/* Terms and Conditions Acceptance */}
      <div className="card mb-3 border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">Terms and Conditions</h6>
        </div>
        <div className="card-body">
          <div className="form-check">
            <input
              className={`form-check-input ${errors.termsAccepted ? 'is-invalid' : ''}`}
              type="checkbox"
              id="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => handleTermsChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="termsAccepted">
              I have read and agree to the{' '}
              <Link 
                to={`/applications/terms${formData.universityId ? `?universityId=${formData.universityId}` : ''}`} 
                target="_blank" 
                className="text-primary"
              >
                Terms and Conditions, Privacy Policy, and Declaration
                <ExternalLink size={14} className="ms-1" />
              </Link>
              {' '}(Sections G - H)
            </label>
            {errors.termsAccepted && (
              <div className="invalid-feedback d-block">{errors.termsAccepted}</div>
            )}
          </div>
          <small className="text-muted d-block mt-2">
            By checking this box, you confirm that you have read, understood, and agree to all terms and conditions, 
            privacy policies, and declarations outlined in Sections F, G, and H of the application form.
          </small>
        </div>
      </div>

      {/* Agent Details Section */}
      {user?.agentProfile && (
        <div className="card mb-3 border-primary">
          <div className="card-header bg-primary text-white">
            <h6 className="mb-0 fw-semibold">
              <User size={18} className="me-2" />
              Agent Details
            </h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <User size={18} className="text-primary me-2" />
                  <div>
                    <strong>Agent Name:</strong>
                    <div>{user.firstName} {user.lastName}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <Building size={18} className="text-primary me-2" />
                  <div>
                    <strong>Agency Name:</strong>
                    <div>{user.agentProfile.agencyName}</div>
                  </div>
                </div>
              </div>
              {user.agentProfile.licenseNumber && (
                <div className="col-md-6 mb-3">
                  <div>
                    <strong>License Number:</strong>
                    <div>{user.agentProfile.licenseNumber}</div>
                  </div>
                </div>
              )}
              <div className="col-md-6 mb-3">
                <div className="d-flex align-items-center mb-2">
                  <Mail size={18} className="text-primary me-2" />
                  <div>
                    <strong>Agent Email:</strong>
                    <div>{user.email}</div>
                  </div>
                </div>
              </div>
              {user.agentCode && (
                <div className="col-md-6 mb-3">
                  <div>
                    <strong>Agent Code:</strong>
                    <div>{user.agentCode}</div>
                  </div>
                </div>
              )}
              <div className="col-md-6 mb-3">
                <div>
                  <strong>Agent Status:</strong>
                  <div>
                    <span className={`badge ${user.agentProfile.isVerified ? 'bg-success' : 'bg-warning'}`}>
                      {user.agentProfile.isVerified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-info mt-3 mb-0">
              <small>
                <strong>Note:</strong> This application is being submitted on behalf of the student by the above agent/consultant. 
                The agent confirms that all information provided is accurate to the best of their knowledge.
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-warning">
          <strong>Please fix the following errors before submitting:</strong>
          <ul className="mb-0 mt-2">
            {Object.entries(errors).map(([key, value]) => (
              value && <li key={key}>{value}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
